import "../styles/pipeline.css";
import { PIPELINE_STAGES } from "../data/moduleTransitions.js";
import useDealStore from "../store/dealStore";
import useGhlStore from "../store/ghlStore";

// Task 11: GHL stage → progress percentage mapping
const GHL_STAGE_PROGRESS = {
  Lead: 10, Qualified: 30, 'Call Scheduled': 50,
  Negotiation: 65, 'Under Contract': 80, Closed: 100,
};

function PipelineTracker() {
  const { dealState, activeModule, getModuleStatus, navigateToModule } = useDealStore();
  const { currentStage: ghlStage, stageProgress: ghlProgress, isConfigured: ghlConfigured } = useGhlStore();

  // Compute current position in pipeline
  const currentStageIndex = PIPELINE_STAGES.findIndex(s => {
    if (activeModule === 'entry') return s.id === 'entry';
    if (typeof activeModule === 'number') return s.id === activeModule;
    return false;
  });
  const displayStage = currentStageIndex >= 0 ? currentStageIndex + 1 : 1;

  // Task 11: Drive progress bar from GHL if configured, otherwise use module position
  const progressPct = ghlConfigured && ghlStage
    ? (GHL_STAGE_PROGRESS[ghlStage] ?? ghlProgress ?? Math.round((displayStage / PIPELINE_STAGES.length) * 100))
    : Math.round((displayStage / PIPELINE_STAGES.length) * 100);

  const progressLabel = ghlConfigured && ghlStage
    ? `M2M: ${ghlStage} — ${progressPct}%`
    : `Step ${displayStage} of ${PIPELINE_STAGES.length}`;

  return (
    <div className="pipeline-tracker">
      <div className="pipeline-header">
        <h3>Deal Pipeline</h3>
        <div className="pipeline-progress-info">
          <span>{progressLabel}</span>
          <div className="mini-progress-bar">
            <div
              className="mini-progress-fill"
              style={{ width: `${progressPct}%`, transition: 'width 0.4s ease' }}
            />
          </div>
        </div>
      </div>

      <div className="pipeline-stages">
        {PIPELINE_STAGES.map((stage, idx) => {
          // Entry stage special handling
          if (stage.id === 'entry') {
            const isCurrent = activeModule === 'entry' || activeModule === 0;
            return (
              <div
                key="entry"
                className={`pipeline-stage ${isCurrent ? 'current' : ''}`}
                onClick={() => navigateToModule('entry')}
                title="Deal Entry — starting point"
              >
                <div className="stage-indicator">🎯</div>
                <div className="stage-info">
                  <div className="stage-number">E</div>
                  <div className="stage-name">{stage.shortName}</div>
                </div>
                {isCurrent && <div className="current-arrow">▼</div>}
              </div>
            );
          }

          const status = getModuleStatus(stage.id);
          const isCurrent = activeModule === stage.id;
          const isCompleted = status.isComplete;
          const isAvailable = status.canEnter;

          return (
            <div
              key={stage.id}
              className={`pipeline-stage ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''} ${!isAvailable ? 'unavailable' : ''}`}
              onClick={() => isAvailable && navigateToModule(stage.id)}
              title={`${stage.name} — ${isCompleted ? 'Completed' : isCurrent ? 'Current' : isAvailable ? 'Available' : 'Locked'}`}
            >
              <div className="stage-indicator">
                {isCompleted ? '✓' : idx}
              </div>
              <div className="stage-info">
                <div className="stage-number">{idx}</div>
                <div className="stage-name">{stage.shortName}</div>
              </div>
              {isCurrent && <div className="current-arrow">▼</div>}
            </div>
          );
        })}
      </div>

      <div className="pipeline-legend">
        <div className="legend-item">
          <div className="legend-indicator completed"></div>
          <span>Completed</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator current"></div>
          <span>Current</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator available"></div>
          <span>Available</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator unavailable"></div>
          <span>Locked</span>
        </div>
      </div>
    </div>
  );
}

export default PipelineTracker;